import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  addAlways,
  addAnswer,
  addChapter,
  addComment,
  addGuidesExamples,
  addOperator,
  addStep,
  addTrigger,
  askToDelete,
  changeChapterOrderNumber,
  changeComment,
  changeMode,
  changePossibleAnswerOrderNumber,
  changeQuestionOrderNumber,
  changeRichText,
  deleteElement,
  ElementToDelete,
  findConditionToChange,
  negateTrigger,
  removeChapter,
  removeStep,
  renameAnswer,
  renameChapter,
  renameStep,
  resetPopUpType,
  RichTextFocus,
  setAddElement,
  setAnswerValue,
  setClosePopUp,
  setCurrentChapter,
  setCurrentStep,
  setGuideExample,
  setPopupType,
  setProducerUserManualCreation,
  setQuestionValue,
  setRichTextFocus,
  setSelectedTrigger,
  setShowPopUp,
  setShowPopUpOnly
} from "feature/producer/actions";
import {
  Chapter,
  Module,
  PossibleAnswer,
  Question,
  Trigger,
  TriggerBody,
} from "types/type.module";
import { Direction, GuidanceOrExample, StepContent } from "types/types";
import { ElementLabelText } from "../Components/Atoms/ElementLabel";
import {
  ConditionTranslator,
  labelTrigger,
} from "../Logic/ConditionTranslator";
import {
  removeTrigger,
  toggleTrigger,
  writeOperatorAndTrigger,
  writeTrigger,
} from "../Logic/ConditionWriter";
import {
  deleteChapter,
  deleteExample,
  deleteGuidance,
  deletePossibleAnswer,
  deleteQuestion,
  getModule,
  patchChapter,
  patchExample,
  patchGuidance,
  patchPossibleAnswers,
  patchQuestion,
  patchUserManual,
  PayloadPostPossibleAnswer,
  PayloadPostQuestion,
  postChapter,
  postExample,
  postGuidance,
  postPossibleAnswers,
  postQuestion,
} from "../Logic/ModuleApi";
import { mapModule, negateTriggers } from "../Logic/ModuleTranslator";
import { JSONToArray } from "../Logic/RichTextTranslator";
import { navigateTriggers } from "../Logic/Steps";
import { RootState } from "./store";

type Snapshot = (
  | Partial<Chapter>
  | Partial<Question>
  | Partial<PossibleAnswer>
  | Partial<GuidanceOrExample>
  | Partial<GuidanceOrExample>
) & {
  question_id?: string;
  possible_answer_id?: string;
  chapter_id?: string;
  module_id?: string;
};

type BackendEvent = {
  type: "chapter" | "question" | "possible_answer" | "answer";
  event: "post" | "patch" | "delete" | "patched";
  snapshot?: Snapshot | string | null;
  guidances?: Array<GuidanceOrExample | string>;
  examples?: Array<GuidanceOrExample | string>;
  elementModified?: boolean;
};

type BackendEvents = Record<string, BackendEvent>;

// TODO: update this name to something more descriptive
export type TriggerVariation = {
  answer?: any;
  label?: string;
  trigger: TriggerBody;
  chapter?: string;
  question?: string;
};

type ProhibitedCallbackInRedux = { callback: (arg: any, arg2: any) => void };

type ConditionToAdd = {
  label: string;
  newTrigger: Trigger;
  pos: { chapter: number; question: number };
  ids: { chapter: string; question?: string };
};

export type ProducerState = {
  currentModule: Module | null;
  mode: "chapters";
  currentStep: string | null;
  showAddElement: boolean;
  showPopUp: boolean;
  popUpType: "Reference" | "Term" | "Link" | "Trigger" | null;
  triggers: Array<{
    trigger: TriggerVariation;
    label?: string;
    answer?: string;
  }>;
  searchBarShowing: boolean;
  selectedTrigger?: TriggerVariation;
  triggerToAdd: {
    chapter: number;
    condition: Trigger;
    directions: Direction[];
    operator: "and" | "or" | "not";
    step: number;
  } | null;
  newBeEvents: BackendEvents;
  linkToAdd?: ProhibitedCallbackInRedux;
  refToAdd?: ProhibitedCallbackInRedux;
  termToAdd?: ProhibitedCallbackInRedux;
  richTextFocus?: RichTextFocus;
  deleteElement?: ElementToDelete;
  currentChapter?: string;
  conditionToChange?: {
    toAdd: ConditionToAdd[];
    deletedStatus: boolean;
  };
  userManualCreation?: boolean;
};

const initialState: ProducerState = {
  currentModule: null,
  mode: "chapters",
  currentStep: null,
  showAddElement: false,
  showPopUp: false,
  popUpType: null,
  triggers: [],
  searchBarShowing: false,
  triggerToAdd: null,
  newBeEvents: {},
  userManualCreation: false,
};

/*
  @function producerSlice
  create producer redux state, this will handle the current used module
*/
export const producerSlice = createSlice({
  name: "producer",
  initialState,
  reducers: {
    setSearchBarShowing: (state, action: PayloadAction<boolean>) => {
      state.searchBarShowing = action.payload;
    },
    setLinkToAdd: (state, action: PayloadAction<ProhibitedCallbackInRedux>) => {
      state.showPopUp = false;
      state.popUpType = "Link";
      state.linkToAdd = action.payload;
    },
    setRefToAdd: (state, action: PayloadAction<ProhibitedCallbackInRedux>) => {
      state.showPopUp = true;
      state.popUpType = "Reference";
      state.refToAdd = action.payload;
    },
    setTermToAdd: (state, action: PayloadAction<ProhibitedCallbackInRedux>) => {
      state.showPopUp = false;
      state.popUpType = "Term";
      state.termToAdd = action.payload;
    },
    setConditionToChange: (state) => {
      state.conditionToChange = undefined;
    },
    removeFrontAnswers: (state, {payload}) => {
      const answers = state.currentModule?.chapters[payload.chapterIndex-1].questions[payload.questionIndex-1].possible_answers.filter((pa) => {
        if(pa.possible_answer_id !== undefined) {
          return true;
        } else {
          pa?.frontAnswerId && delete state.newBeEvents[pa?.frontAnswerId];
          return false;
        }
      });
      if(state.currentModule) {
        if(state.currentModule.chapters) {
          if(state.currentModule.chapters[payload.chapterIndex-1]) {
            if(state.currentModule.chapters[payload.chapterIndex-1].questions[payload.questionIndex-1] && answers != undefined) {
              state.currentModule.chapters[payload.chapterIndex-1].questions[payload.questionIndex-1].possible_answers = answers;
            }
          }
        }
      }
    } 
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentStep, (state, { payload }) => {
        state.currentStep = payload.currentStep;
      })
      .addCase(setAddElement, (state) => {
        state.showAddElement = !state.showAddElement;
      })
      .addCase(addChapter, (state, { payload }) => {
        if (!state.currentModule) return;

        const { chapterPosition } = payload;

        state.currentModule.chapters.forEach((c) => {
          if (c.order_number >= chapterPosition) c.order_number++;
        });

        const newId = idGenerator();

        state.currentModule.chapters.push({
          // TODO: should the chapter_id be null?
          chapter_id: newId,
          module_id: state.currentModule.module_id,
          name: "Insert Name here",
          order_number: chapterPosition,
          frontChapterId: newId,
          questions: [],
        });

        state.newBeEvents[newId] = { type: "chapter", event: "post" };

        state.currentModule.chapters.sort((p, n) => {
          return p.order_number - n.order_number;
        });
      })
      .addCase(renameChapter, (state, { payload }) => {
        if (!state.currentModule) return;
        const { chapterPosition, chapterId, newName } = payload;

        const targetChapter = state.currentModule.chapters.find(
          (c) => c.order_number === chapterPosition
        );
        const tc_copy = { ...targetChapter, name: newName };
        if (chapterId !== undefined && !(chapterId in state.newBeEvents)) {
          state.newBeEvents[chapterId] = {
            type: "chapter",
            event: "patch",
            snapshot: tc_copy,
          };
        }
      })
      .addCase(askToDelete, (state, { payload }) => {
        state.deleteElement = payload;
      })
      .addCase(addChapterToBackend.fulfilled, (state, { payload }) => {
        if (!state.currentModule) return;
        const { frontChapterId, response } = payload;

        if ("error" in payload || !response) return;

        if (frontChapterId) {
          const { [frontChapterId]: _, ...withoutTargetChapter } =
            state.newBeEvents;
          state.newBeEvents = withoutTargetChapter;
        }

        state.currentModule.chapters.map((c) => {
          if (c.frontChapterId === frontChapterId) {
            c.chapter_id = response.chapter_id;
          }
        });
      })
      .addCase(addStep, (state, { payload }) => {
        if (!state.currentModule) return;

        const { chapter, position, type } = payload;
        let index = 0;
        let chapter_id = "";

        state.currentModule.chapters.forEach((c, i) => {
          if (c.order_number === chapter && c.chapter_id) {
            chapter_id = c.chapter_id;
            index = i;
          }
        });

        const chapterObj = state.currentModule.chapters[index];
        chapterObj.questions.forEach((q) => {
          if (q.order_number >= position) q.order_number++;
        });

        const frontStepId = idGenerator();
        chapterObj.questions.push({
          chapter_id,
          comment: null,
          examples: [],
          guidances: [],
          content: JSON.stringify({ text: [["p", {}, "Insert Name here"]] }),
          order_number: position,
          possible_answers: [],
          frontQuestionId: frontStepId,
          conclusion: type === "conclusion" ? true : false,
          trigger: { and: [{ or: [] }] },
          print: false,
          multiple: false,
          should_be_hidden: false,
        });

        chapterObj.questions.sort((p, n) => {
          return p.order_number - n.order_number;
        });

        state.newBeEvents[frontStepId] = { type: "question", event: "post" };

        state.currentModule.chapters[index] = chapterObj;
      })
      .addCase(addComment, (state, { payload }) => {
        if (!state.currentModule) return;

        const { chapterIndex, questionIndex, answerId } = payload;

        if (answerId !== undefined) {
          state.currentModule.chapters[chapterIndex].questions[
            questionIndex
          ].possible_answers.forEach((a) => {
            if (
              a.possible_answer_id === answerId &&
              a.possible_answer_id !== undefined
            ) {
              a.comment = "";
            }
            if (a.frontAnswerId === answerId && a.frontAnswerId !== undefined) {
              a.comment = "";
            }
          });
        } else {
          const question =
            state.currentModule.chapters[chapterIndex].questions[questionIndex];
          question.comment = "";
        }
      })
      .addCase(setRichTextFocus, (state, { payload }) => {
        state.userManualCreation = false;
        if ("link" in payload || "term" in payload || "reference" in payload) {
          state.richTextFocus = payload;
        } else {
          const { chapter, question, answer, guide, example } = payload;

          if (chapter === undefined) {
            state.richTextFocus = undefined;
          } else {
            state.richTextFocus = {
              chapter: chapter,
              question: question,
              answer: answer,
              guide: guide,
              example: example,
            };
          }
        }
      })
      .addCase(setQuestionValue, (state, { payload }) => {
        if (!state.currentModule) return;

        const { questionId, valueName, value, frontQuestionId } = payload;

        if (questionId !== undefined) {
          state.newBeEvents = {
            ...state.newBeEvents,
            [questionId]: {
              ...state.newBeEvents[questionId],
              type: "question",
              event: "patch",
            },
          };
        }

        state.currentModule.chapters.map((c) => {
          c.questions.map((q) => {
            if (q.question_id === questionId && questionId !== undefined) {
              if (!state.newBeEvents[questionId].snapshot) {
                const q_copy = { ...q };
                if (typeof q_copy.content === "string") {
                  q_copy.content = JSON.parse(q_copy.content);
                }
                state.newBeEvents[questionId].snapshot = q_copy;
              }
              q[valueName] = value;
            }
            if (
              q.frontQuestionId === frontQuestionId &&
              frontQuestionId !== undefined
            ) {
              q[valueName] = value;
            }
          });
        });
      })
      .addCase(setCurrentChapter, (state, { payload }) => {
        state.currentChapter = payload;
      })
      .addCase(renameStep, (state, { payload }) => {
        if (!state.currentModule) return;
        const { chapterPosition, stepPosition, value, stepId } = payload;

        if (stepId && state.newBeEvents[stepId] === undefined) {
          state.newBeEvents[stepId] = {
            ...state.newBeEvents[stepId],
            type: "question",
            event: "patched",
          };
        }
        state.currentModule.chapters.forEach((c) => {
          if (c.order_number === chapterPosition) {
            c.questions.forEach((q) => {
              if (q.order_number === stepPosition) {
                if (
                  stepId !== undefined &&
                  state.newBeEvents[stepId] === undefined
                ) {
                  const q_copy = { ...q };
                  if (typeof q_copy.content === "string") {
                    q_copy.content = JSON.parse(q_copy.content);
                  }
                  state.newBeEvents[stepId].snapshot = q_copy;
                }
                q.content = value;
              }
            });
          }
        });
      })
      .addCase(addGuidesExamples, (state, { payload }) => {
        if (!state.currentModule) return;
        const { questionId, chapterId, id, elementIndex, isQuestion } = payload;

        if (isQuestion) {
          let order_number = 0;
          state.currentModule.chapters[chapterId].questions[questionId][
            id
          ].forEach((el) => {
            if (el.order_number > order_number) {
              order_number = el.order_number;
            }
          });

          state.currentModule.chapters[chapterId].questions[questionId][
            id
          ].push({
            content: {},
            frontId: idGenerator(),
            order_number: order_number + 1,
            type: id,
          });
        } else {
          let order_number = 0;
          state.currentModule.chapters[chapterId].questions[
            questionId
          ].possible_answers[elementIndex][id].forEach((el) => {
            if (el.order_number > order_number) {
              order_number = el.order_number;
            }
          });

          state.currentModule.chapters[chapterId].questions[
            questionId
          ].possible_answers[elementIndex][id].push({
            content: {},
            type: id,
            frontId: idGenerator(),
            order_number: order_number,
            possible_answer_id:
              state.currentModule.chapters[chapterId].questions[questionId]
                .possible_answers[elementIndex].possible_answer_id,
          });
        }
      })
      .addCase(setGuideExample, (state, { payload }) => {
        if (!state.currentModule) return;
        const {
          chapterPosition,
          questionPosition,
          answerPosition,
          type,
          value,
          isQuestion,
          id,
        } = payload;

        if (isQuestion) {
          state.currentModule.chapters[chapterPosition].questions[
            questionPosition
          ][type].map((guidanceOrExample) => {
            const transformedId = type.slice(0, -1) + "_id"; // guidances -> guidance_id
            if (transformedId in guidanceOrExample) {
              if (
                guidanceOrExample[transformedId as keyof GuidanceOrExample] ===
                id
              ) {
                guidanceOrExample.content = value;
              }
            } else {
              if (guidanceOrExample.frontId === id) {
                guidanceOrExample.content = value;
              }
            }
          });
          const questionId =
            state.currentModule.chapters[chapterPosition].questions[
              questionPosition
            ].question_id;

          if (questionId && !state.newBeEvents[questionId]) {
            state.newBeEvents[questionId] = {
              type: "question",
              event: "patch",
              guidances: [],
              examples: [],
            };
          }

          if (questionId && !state.newBeEvents[questionId][type]) {
            state.newBeEvents[questionId][type] = [];
          }
          if (
            questionId &&
            state.newBeEvents[questionId][type]?.findIndex((e) => e === id) ===
              -1
          ) {
            state.newBeEvents[questionId][type]?.push(id);
          }
        } else if (typeof answerPosition === "number") {
          state.currentModule.chapters[chapterPosition].questions[
            questionPosition
          ].possible_answers[answerPosition][type].map((guidanceOrExample) => {
            const transformedId = type.slice(0, -1) + "_id"; // guidances -> guidance_id
            if (transformedId in guidanceOrExample) {
              if (
                guidanceOrExample[transformedId as keyof GuidanceOrExample] ===
                id
              ) {
                guidanceOrExample.content = value;
              }
            } else {
              if (guidanceOrExample.frontId === id) {
                guidanceOrExample.content = value;
              }
            }
          });

          const possibleAnswerId =
            state.currentModule.chapters[chapterPosition].questions[
              questionPosition
            ].possible_answers[answerPosition].possible_answer_id;
          if (possibleAnswerId && !state.newBeEvents[possibleAnswerId]) {
            state.newBeEvents[possibleAnswerId] = {
              type: "answer",
              event: "patch",
              guidances: [],
              examples: [],
            };
          }

          if (possibleAnswerId) {
            if (!state.newBeEvents[possibleAnswerId][type]) {
              state.newBeEvents[possibleAnswerId][type] = [];
            }
            if (
              state.newBeEvents[possibleAnswerId][type]?.findIndex(
                (e) => e === id
              ) === -1
            ) {
              state.newBeEvents[possibleAnswerId][type]?.push(id);
            }
          }
        }
      })
      .addCase(addOperator, (state, { payload }) => {
        if (!state.currentModule) return;
        const { directions, operator, step, chapter } = payload;
        const condition =
          state.currentModule.chapters[chapter].questions[step].trigger;
        if (state.searchBarShowing && state.selectedTrigger) {
          const trigger = state.selectedTrigger.trigger;
          const questionId =
            state.currentModule.chapters[chapter].questions[step].question_id;

          if (questionId && !state.newBeEvents[questionId]) {
            state.newBeEvents[questionId] = {
              event: "patch",
              type: "question",
              snapshot: state.currentModule.chapters[chapter].questions[step],
            };
          }

          writeOperatorAndTrigger(directions, operator, condition, trigger, 0);

          state.searchBarShowing = false;
        } else {
          // TODO: This is absolutely the most possible wrong place for side-effect like this
          document.getElementById("searchBarInput")?.focus();
          state.popUpType = "Trigger";
          state.triggerToAdd = {
            directions: directions,
            operator: operator,
            step: step,
            chapter: chapter,
            condition: condition,
          };
        }
      })
      .addCase(setSelectedTrigger, (state, { payload }) => {
        if (!state.currentModule) return;
        const { trigger } = payload;

        state.selectedTrigger = trigger;

        state.popUpType = null;
        if (state.triggerToAdd) {
          const condition =
            state.currentModule.chapters[state.triggerToAdd.chapter].questions[
              state.triggerToAdd.step
            ].trigger;

          const questionId =
            state.currentModule.chapters[state.triggerToAdd.chapter].questions[
              state.triggerToAdd.step
            ].question_id;

          writeOperatorAndTrigger(
            state.triggerToAdd.directions,
            state.triggerToAdd.operator,
            condition,
            trigger.trigger,
            0
          );

          if (questionId && !state.newBeEvents[questionId]) {
            state.newBeEvents[questionId] = {
              event: "patch",
              type: "question",
            };
          }
          state.triggerToAdd = null;
        }
      })
      .addCase(deleteElement, (state, { payload }) => {
        if (!state.currentModule) return;
        const { directions, step, chapter } = payload;
        const condition =
          state.currentModule.chapters[chapter].questions[step].trigger;

        const questionId =
          state.currentModule.chapters[chapter].questions[step].question_id;

        if (questionId && !state.newBeEvents[questionId]) {
          state.newBeEvents[questionId] = {
            event: "patch",
            type: "question",
            snapshot: state.currentModule.chapters[chapter].questions[step],
          };
        }

        if (directions.length === 0) {
          state.currentModule.chapters[chapter].questions[step].trigger = {
            and: [{ or: [] }],
          };
        } else {
          removeTrigger(directions, condition);
        }
      })
      .addCase(addAnswer, (state, { payload }) => {
        if (!state.currentModule) return;
        const { chapter, step, order_number } = payload;

        state.currentModule.chapters[chapter].questions[
          step
        ].possible_answers.forEach((p) => {
          if (p.order_number >= order_number) p.order_number++;
        });

        const frontAnswerId = idGenerator();

        state.currentModule.chapters[chapter].questions[
          step
        ].possible_answers.push({
          content: JSON.stringify({ text: [["p", {}, "Insert Name here"]] }),
          order_number: order_number,
          frontAnswerId: frontAnswerId,
          guidances: [],
          examples: [],
          single: false,
        });

        state.newBeEvents[frontAnswerId] = { type: "answer", event: "post" };
      })
      .addCase(setAnswerValue, (state, { payload }) => {
        if (!state.currentModule) return;

        const { answerId, valueName, frontAnswerId, value } = payload;

        if (
          answerId !== undefined &&
          state.newBeEvents[answerId] === undefined
        ) {
          state.newBeEvents = {
            ...state.newBeEvents,
            [answerId]: {
              ...state.newBeEvents[answerId],
              type: "answer",
              event: "patch",
            },
          };
        }

        state.currentModule.chapters.map((c) => {
          c.questions.map((q) => {
            q.possible_answers.map((a) => {
              if (a.possible_answer_id === answerId && answerId !== undefined) {
                if (!state.newBeEvents[answerId].snapshot) {
                  const a_copy = { ...a };
                  if (typeof a_copy.content === "string") {
                    a_copy.content = JSON.parse(a_copy.content);
                  }
                  state.newBeEvents[answerId].snapshot = a_copy;
                }
                a[valueName] = value;
              }
              if (
                a.frontAnswerId === frontAnswerId &&
                frontAnswerId !== undefined
              ) {
                a[valueName] = value;
              }
            });
          });
        });
      })
      .addCase(renameAnswer, (state, { payload }) => {
        if (!state.currentModule) return;

        const { newName, answer, frontAnswerId } = payload;

        if (answer !== undefined && state.newBeEvents[answer] === undefined) {
          state.newBeEvents[answer] = {
            ...state.newBeEvents[answer],
            type: "answer",
            event: "patched",
          };
        }
        state.currentModule.chapters.forEach((c) => {
          c.questions.forEach((q) => {
            q.possible_answers.forEach((a) => {
              if (a.possible_answer_id === answer && answer !== undefined) {
                if (!state.newBeEvents[answer].snapshot) {
                  const a_copy = { ...a };
                  if (typeof a_copy.content === "string") {
                    a_copy.content = JSON.parse(a_copy.content);
                  }
                  state.newBeEvents[answer].snapshot = a_copy;
                }
                a.content = newName;
              }
              if (
                a.frontAnswerId === frontAnswerId &&
                frontAnswerId !== undefined
              ) {
                a.content = newName;
              }
            });
          });
        });
      })
      .addCase(changePossibleAnswerOrderNumber, (state, { payload }) => {
        if (!state.currentModule) return;

        const { orderNumber, answerId, frontAnswerId, oldOrderNumber } =
          payload;

        state.currentModule.chapters.forEach((c) => {
          c.questions.map((q) => {
            q.possible_answers.map((a) => {
              if (a.order_number === orderNumber)
                a.order_number = oldOrderNumber;
            });
          });
        });

        if (
          answerId !== undefined &&
          state.newBeEvents[answerId] === undefined
        ) {
          state.newBeEvents = {
            ...state.newBeEvents,
            [answerId]: {
              ...state.newBeEvents[answerId],
              type: "answer",
              event: "patch",
            },
          };
        }

        state.currentModule.chapters.forEach((c) => {
          c.questions.map((q) => {
            q.possible_answers.map((a) => {
              if (a.possible_answer_id === answerId && answerId !== undefined) {
                if (!state.newBeEvents[answerId].snapshot) {
                  const a_copy = { ...a };
                  if (typeof a_copy.content === "string") {
                    a_copy.content = JSON.parse(a_copy.content);
                  }
                  state.newBeEvents[answerId].snapshot = a_copy;
                }
                a.order_number = orderNumber;
              }
              if (
                a.frontAnswerId === frontAnswerId &&
                frontAnswerId !== undefined
              ) {
                a.order_number = orderNumber;
              }
            });
          });
        });
      })
      .addCase(findConditionToChange, (state, { payload }) => {
        if (!state.currentModule) return;

        const { chapter, question, answer } = payload;
        const toAdd: ConditionToAdd[] = [];

        state.currentModule.chapters.forEach((c, c_i) => {
          c.questions.forEach((q, q_i) => {
            const result = { result: false };

            const newTrigger = q.trigger;

            if (chapter && question && answer) {
              isTriggerInConditionAndAddDeleteCallBack(
                { chapter: chapter, question: question, answer: answer },
                newTrigger,
                result
              );
            }

            if (result.result) {
              toAdd.push({
                label: ElementLabelText({
                  chapter: c.order_number,
                  question: q.order_number,
                  conclusion: q.conclusion,
                }),
                newTrigger: newTrigger,
                pos: { chapter: c_i, question: q_i },
                ids: { chapter: c.chapter_id || "", question: q.question_id },
              });
            }
          });

          state.conditionToChange = { toAdd: toAdd, deletedStatus: false };
        });
      })
      .addCase(changeComment, (state, { payload }) => {
        if (!state.currentModule) return;

        const { chapterIndex, questionIndex, answerId, comment } = payload;

        if (answerId !== undefined) {
          let answer = null;
          state.currentModule.chapters[chapterIndex].questions[
            questionIndex
          ].possible_answers.forEach((a) => {
            if (
              a.possible_answer_id === answerId &&
              a.possible_answer_id !== undefined
            ) {
              a.comment = comment;
              answer = a;
            }
            if (a.frontAnswerId === answerId && a.frontAnswerId !== undefined) {
              a.comment = comment;
              answer = a;
            }
          });

          if (!state.newBeEvents[answerId]) {
            state.newBeEvents[answerId] = {
              event: "patch",
              type: "possible_answer",
              snapshot: answer,
            };
          }
        } else {
          const question =
            state.currentModule.chapters[chapterIndex].questions[questionIndex];
          question.comment = comment;

          if (
            question.question_id &&
            !state.newBeEvents[question.question_id]
          ) {
            state.newBeEvents[question.question_id] = {
              event: "patch",
              type: "question",
              snapshot: question,
            };
          }
        }
      })
      .addCase(changeQuestionOrderNumber, (state, { payload }) => {
        if (!state.currentModule) return;
        const { order_number, questionId, oldOrderNumber, frontQuestionId } =
          payload;

        let c_index = 0;
        let q_index = 0;

        state.currentModule.chapters.forEach((c, c_i) => {
          c.questions.forEach((q, q_i) => {
            if (q.question_id === questionId && questionId !== undefined) {
              c_index = c_i;
              q_index = q_i;
            }
            if (
              q.frontQuestionId === frontQuestionId &&
              frontQuestionId !== undefined
            ) {
              c_index = c_i;
              q_index = q_i;
            }
          });
        });

        state.currentModule.chapters[c_index].questions.map((q) => {
          if (q.order_number === order_number) {
            q.order_number = oldOrderNumber;
          }
        });

        const question =
          state.currentModule.chapters[c_index].questions[q_index];

        if (
          questionId !== undefined &&
          state.newBeEvents[questionId] === undefined
        ) {
          state.newBeEvents[questionId] = {
            ...state.newBeEvents[questionId],
            type: "question",
            event: "patch",
          };
        }

        if (question.question_id === questionId && questionId !== undefined) {
          if (
            questionId !== undefined &&
            !state.newBeEvents[questionId].snapshot
          ) {
            const q_copy = { ...question };
            if (typeof q_copy.content === "string") {
              q_copy.content = JSON.parse(q_copy.content);
            }
            state.newBeEvents[questionId].snapshot = q_copy;
          }
          question.order_number = order_number;
        }

        if (
          question.frontQuestionId === frontQuestionId &&
          frontQuestionId !== undefined
        ) {
          question.order_number = order_number;
        }

        state.currentModule.chapters[c_index].questions.sort((p, n) => {
          return p.order_number - n.order_number;
        });
      })
      .addCase(changeChapterOrderNumber, (state, { payload }) => {
        if (!state.currentModule) return;

        const { order_number, chapterId, oldOrderNumber, frontChapterId } =
          payload;
        let snapshot: Snapshot | undefined = undefined;

        state.currentModule.chapters.forEach((c) => {
          if (c.order_number === order_number) {
            c.order_number = oldOrderNumber;
          }
        });

        state.currentModule.chapters.forEach((c) => {
          if (c.chapter_id === chapterId && chapterId !== undefined) {
            snapshot = JSON.parse(JSON.stringify(c));
            c.order_number = order_number;
          }
          if (
            c.frontChapterId === frontChapterId &&
            frontChapterId !== undefined
          ) {
            c.order_number = order_number;
          }
        });

        state.currentModule.chapters.sort((p, n) => {
          return p.order_number - n.order_number;
        });

        if (chapterId !== undefined && !(chapterId in state.newBeEvents)) {
          state.newBeEvents[chapterId] = {
            type: "chapter",
            event: "patch",
            snapshot: snapshot,
          };
        }
      })
      .addCase(changeRichText, (state, { payload }) => {
        if (!state.currentModule) return;
    
        const position = state.richTextFocus;
    
        if (!position) return;
        
        const element: Partial<Snapshot> = {};
    
        if (
          position.answer === undefined &&
          position.chapter !== undefined &&
          position.question !== undefined
        ) {
          element.question_id =
            state.currentModule.chapters[position.chapter].questions[
              position.question
            ].question_id;
        } else if (position.chapter !== undefined && position.question !== undefined && position.answer !== undefined) {
          element.possible_answer_id =
            state.currentModule.chapters[position.chapter].questions[
              position.question
            ].possible_answers[position.answer].possible_answer_id;
        }
    
        if (position.answer !== undefined && element.possible_answer_id) {
          if (state.newBeEvents[element.possible_answer_id] === undefined) {
            // @ts-ignore
            state.newBeEvents[element.possible_answer_id] = {
              type: "answer",
              event: "patch",
              snapshot: element,
              elementModified: true,
              guidances: [],
              examples: [],
            };
          }
        } else {
          if (
            element.question_id &&
            state.newBeEvents[element.question_id] === undefined
          ) {
            state.newBeEvents[element.question_id] = {
              type: "question",
              event: "patch",
              snapshot: JSON.stringify(element),
              elementModified: true,
              guidances: [],
              examples: [],
            };
          }
        }
    
        if (position.answer === undefined) {
          if (
            position.guide !== undefined &&
            position.chapter !== undefined &&
            position.question !== undefined
          ) {
            const guide =
              state.currentModule.chapters[position.chapter].questions[
                position.question
              ].guidances[position.guide];
    
            // @ts-ignore
            guide.content = payload;
    
            if (guide.guidance_id && element.question_id) {
              state.newBeEvents[element.question_id].guidances?.push(
                guide.guidance_id
              );
            }
          } else if (
            position.example !== undefined &&
            position.chapter !== undefined &&
            position.question !== undefined
          ) {
            const example =
              state.currentModule.chapters[position.chapter].questions[
                position.question
              ].examples[position.example];
            example.content = payload;
            if (example.example_id && element.question_id) {
              state.newBeEvents[element.question_id].examples?.push(
                example.example_id
              );
            }
          } else if (position.chapter !== undefined && position.question !== undefined) {
            state.currentModule.chapters[position.chapter].questions[
              position.question
            ].content = payload;
          }
        }
    
        if (position.answer !== undefined) {
          if (
            position.guide !== undefined &&
            position.chapter !== undefined &&
            position.question !== undefined &&
            position.answer !== undefined
          ) {
            const guide =
              state.currentModule.chapters[position.chapter].questions[
                position.question
              ].possible_answers[position.answer].guidances[position.guide];
            guide.content = payload;
            if (guide.guidance_id && element.possible_answer_id) {
              state.newBeEvents[element.possible_answer_id].guidances?.push(
                guide.guidance_id
              );
            }
          } else if (
            position.example !== undefined &&
            position.chapter !== undefined &&
            position.question !== undefined &&
            position.answer !== undefined
          ) {
            const example =
              state.currentModule.chapters[position.chapter].questions[
                position.question
              ].possible_answers[position.answer].examples[position.example];
            example.content = payload;
            if (example.example_id && element.possible_answer_id) {
              state.newBeEvents[element.possible_answer_id].examples?.push(
                example.example_id
              );
            }
          } else if (position.chapter !== undefined && position.question !== undefined && position.answer !== undefined) {
            state.currentModule.chapters[position.chapter].questions[
              position.question
            ].possible_answers[position.answer].content = payload;
          }
        }
      })

      .addCase(changeChapterInBackend.fulfilled, (state, action) => {
        if ("error" in action.payload) return;

        delete state.newBeEvents[action.payload.chapterId];
      })
      .addCase(changeQuestionInBackend.fulfilled, (state, action) => {
        if ("error" in action.payload || !action.payload) return;

        if (!state.currentModule) return;

        const { questionId, chapterId, guidanceRes, exampleRes } =
          action.payload;

        delete state.newBeEvents[action.payload.questionId];
        state.currentModule.chapters.map((c) => {
          if (c.chapter_id === chapterId) {
            c.questions.map((q) => {
              if (q.question_id === questionId) {
                const isATrigger =
                  state.triggers.filter(
                    (t) =>
                      "question" in t.trigger.trigger &&
                      t.trigger.trigger.question == q.question_id
                  ).length === 1;

                if (q.conclusion && !isATrigger) {
                  state.triggers.push({
                    trigger: {
                      trigger: {
                        chapter: c.chapter_id,
                        question: q.question_id,
                        label: `C.${c.order_number}.${q.order_number}`,
                      },
                      label: `C.${c.order_number}.${q.order_number}`,
                      answer: q.content,
                    },
                  });
                } else if (!q.conclusion && isATrigger) {
                  state.triggers = state.triggers.filter(
                    (t) =>
                      "question" in t.trigger.trigger &&
                      t.trigger.trigger.question !== q.question_id
                  );
                }

                guidanceRes.forEach((g) => {
                  q.guidances.map((toChange) => {
                    if (toChange.frontId === g.frontId) {
                      toChange.guidance_id = g.res.guidance_id;
                    }
                  });
                });

                exampleRes.forEach((g) => {
                  q.examples.map((toChange) => {
                    if (toChange.frontId === g.frontId) {
                      toChange.example_id = g.res.example_id;
                    }
                  });
                });
              }
            });
          }
        });
      })
      .addCase(changePossibleAnswerToBackend.fulfilled, (state, action) => {
        if (!state.currentModule || "error" in action || !action.payload)
          return;

        const { questionId, possibleAnswerId, guidanceRes, exampleRes } =
          action.payload;

        if (possibleAnswerId) {
          delete state.newBeEvents[possibleAnswerId];
        }

        state.currentModule.chapters.map((c) => {
          c.questions.map((q) => {
            if (q.question_id === questionId) {
              q.possible_answers.map((a) => {
                if (a.possible_answer_id === possibleAnswerId && guidanceRes) {
                  guidanceRes.forEach((g) => {
                    a.guidances.map((toChange) => {
                      if (toChange.frontId === g.frontId) {
                        toChange.guidance_id = g.res.guidance_id;
                      }
                    });
                  });

                  exampleRes.forEach((g) => {
                    a.examples.map((toChange) => {
                      if (toChange.frontId === g.frontId) {
                        toChange.example_id = g.res.example_id;
                      }
                    });
                  });
                }
              });
            }
          });
        });
      })
      .addCase(removeChapterInBackend.fulfilled, (state, action) => {
        if (!state.currentModule) return;
        let index = 0;
        let chapter_oder_number = 0;
        const chapterId = action.payload.chapterId;
        const frontChapterId = action.payload.frontChapterId;
        state.currentModule.chapters.map((c, i) => {
          if (c.chapter_id === chapterId && chapterId !== undefined) {
            index = i;
            chapter_oder_number = c.order_number;
          }

          if (
            c.frontChapterId === frontChapterId &&
            frontChapterId !== undefined
          ) {
            index = i;
            chapter_oder_number = c.order_number;
          }
        });

        state.currentModule.chapters.splice(index, 1);

        state.currentModule.chapters.forEach((c) => {
          if (c.order_number > chapter_oder_number) {
            c.order_number -= 1;
          }
        });
      })
      .addCase(removeQuestionInBackend.fulfilled, (state, action) => {
        if (!state.currentModule) return;

        let chapter_i = 0;
        let question_i = 0;
        let question_order_number = 0;
        const questionId = action.payload.questionId;
        const frontQuestionId = action.payload.frontQuestionId;

        if (
          state.conditionToChange &&
          state.conditionToChange.toAdd !== undefined
        ) {
          if (state.conditionToChange.toAdd.length > 0) {
            state.conditionToChange.toAdd.forEach((c) => {
              if (state.currentModule) {
                state.currentModule.chapters[c.pos.chapter].questions[
                  c.pos.question
                ].trigger = c.newTrigger;
              }
            });
            state.conditionToChange.deletedStatus = true;
          }
        }

        state.currentModule.chapters.map((c, c_i) => {
          c.questions.map((q, q_i) => {
            if (q.question_id === questionId && questionId !== undefined) {
              chapter_i = c_i;
              question_i = q_i;
              question_order_number = q.order_number;
            }
            if (
              q.frontQuestionId === frontQuestionId &&
              frontQuestionId !== undefined
            ) {
              chapter_i = c_i;
              question_i = q_i;
              question_order_number = q.order_number;
            }
          });
        });

        state.triggers = state.triggers.filter(
          (el) =>
            "question" in el.trigger.trigger &&
            el.trigger.trigger.question !== questionId
        );

        state.currentModule.chapters[chapter_i].questions.splice(question_i, 1);

        state.currentModule.chapters[chapter_i].questions.forEach((q) => {
          if (q.order_number > question_order_number) {
            q.order_number -= 1;
          }
        });
      })
      .addCase(removePossibleAnswerInBackend.fulfilled, (state, action) => {
        if (!state.currentModule) return;

        let chapter_i = 0;
        let question_i = 0;
        let answer_i = 0;
        let answer_order_number = 0;
        const possibleAnswerId = action.payload.possibleAnswerId;
        const frontAnswerId = action.payload.frontAnswerId;

        state.currentModule.chapters.map((c, c_i) => {
          c.questions.map((q, q_i) => {
            q.possible_answers.map((a, a_i) => {
              if (
                a.possible_answer_id === possibleAnswerId &&
                possibleAnswerId !== undefined
              ) {
                chapter_i = c_i;
                question_i = q_i;
                answer_i = a_i;
                answer_order_number = a.order_number;
              }

              if (
                a.frontAnswerId === frontAnswerId &&
                frontAnswerId !== undefined
              ) {
                chapter_i = c_i;
                question_i = q_i;
                answer_i = a_i;
                answer_order_number = a.order_number;
              }
            });
          });
        });

        state.currentModule.chapters[chapter_i].questions[
          question_i
        ].possible_answers.splice(answer_i, 1);

        state.currentModule.chapters[chapter_i].questions[
          question_i
        ].possible_answers.forEach((a) => {
          if (a.order_number > answer_order_number) {
            a.order_number -= 1;
          }
        });

        state.triggers = state.triggers.filter(
          ({ trigger }) =>
            "answer" in trigger.trigger &&
            trigger.trigger.answer !== possibleAnswerId
        );

        if (
          state.conditionToChange &&
          state.conditionToChange.toAdd.length > 0
        ) {
          state.conditionToChange.toAdd.forEach((c) => {
            if (state.currentModule) {
              state.currentModule.chapters[c.pos.chapter].questions[
                c.pos.question
              ].trigger = c.newTrigger;
            }
          });
          state.conditionToChange.deletedStatus = true;
        }
      })
      .addCase(addQuestionToBackend.fulfilled, (state, action) => {
        if (!state.currentModule) return;
        if ("error" in action.payload.response) return;

        const { frontQuestionId, response } = action.payload;

        if (frontQuestionId) {
          delete state.newBeEvents[frontQuestionId];
        }

        state.currentModule.chapters.map((c) => {
          if (c.chapter_id === response.data.chapter_id) {
            c.questions.map((q) => {
              if (q.frontQuestionId === frontQuestionId) {
                q.question_id = response.data.question_id;
                
                if (q.conclusion) {
                  state.triggers.push({
                    trigger: {
                      trigger: {
                        chapter: c.chapter_id,
                        question: q.question_id,
                        label: `C.${c.order_number}.${q.order_number}`,
                      },
                      label: `C.${c.order_number}.${q.order_number}`,
                      answer: q.content,
                    },
                  });
                }
              }
            });
          }
        });
      })
      .addCase(addPossibleAnswerToBackend.fulfilled, (state, action) => {
        if (!state.currentModule) return;
        if (typeof action.payload.response === "object" && "error" in action.payload.response) return;

        const { frontAnswerId, response, guidanceRes, exampleRes } =
          action.payload;

        if (frontAnswerId) {
          delete state.newBeEvents[frontAnswerId];
        }

        const trigger: {
          trigger: Partial<TriggerVariation>;
          label?: string;
          answer?: string;
        } = {
          trigger: {},
        };

        state.currentModule.chapters.map((c) => {
          c.questions.map((q) => {
            if (q.question_id == response.data.question_id) {
              q.possible_answers.map((a) => {
                if (a.frontAnswerId === frontAnswerId) {
                  trigger.trigger.chapter = c.chapter_id;
                  trigger.trigger.question = q.question_id;
                  trigger.trigger.answer = response.data.possible_answer_id;

                  const label = ElementLabelText({
                    chapter: c.order_number,
                    question: q.order_number,
                    answer: a.order_number,
                  });

                  trigger.trigger.label = label;
                  trigger.label = label;
                  trigger.answer = response.data.content;

                  a.possible_answer_id = response.data.possible_answer_id;
                  if (guidanceRes) {
                    guidanceRes.forEach((g) => {
                      a.guidances.map((toChange) => {
                        if (toChange.frontId === g.frontId) {
                          toChange.guidance_id = g.res.guidance_id;
                        }
                      });
                    });
                  }
                  if (exampleRes) {
                    exampleRes.forEach((g) => {
                      a.examples.map((toChange) => {
                        if (toChange.frontId === g.frontId) {
                          toChange.example_id = g.res.example_id;
                        }
                      });
                    });
                  }
                }
              });
            }
          });
        });

        state.triggers.push({ trigger: trigger });
      })
      .addCase(initializeModule.fulfilled, (state, action) => {
        state.triggers = [];
        const { response } = action.payload;

        if (response) {
          response.chapters.map((c) => {
            const sortedQuestions = c.questions.slice().sort((p, n) => {
              return p.order_number - n.order_number;
            });

            c.questions = sortedQuestions;
          });

          const moduleMap = mapModule(response);

          response.chapters.map((c) => {
            c.questions.map((q) => {
              let trigger: Trigger = {};
              if (
                typeof q.trigger === "string" &&
                Object.keys(JSON.parse(q.trigger)).length > 0
              ) {
                q.trigger = JSON.parse(q.trigger);

                negateTriggers(q.trigger);

                trigger = ConditionTranslator({
                  trigger: JSON.parse(JSON.stringify(q.trigger)),
                  depth: 0,
                });

                trigger = labelTrigger({
                  trigger: trigger,
                  module: response,
                  moduleMap: moduleMap,
                });
              } else {
                trigger = { and: [{ or: [] }] };
              }

              q.trigger = trigger;
              q.possible_answers.map((a) => {
                state.triggers.push({
                  trigger: {
                    trigger: {
                      chapter: c.chapter_id,
                      question: q.question_id,
                      answer: a.possible_answer_id,
                      label: `A.${c.order_number}.${q.order_number}.${a.order_number}`,
                    },
                    label: `A.${c.order_number}.${q.order_number}.${a.order_number}`,
                    answer: a.content,
                  },
                });
              });

              if (q.conclusion) {
                state.triggers.push({
                  trigger: {
                    trigger: {
                      chapter: c.chapter_id,
                      question: q.question_id,
                      label: `C.${c.order_number}.${q.order_number}`,
                    },
                    label: `C.${c.order_number}.${q.order_number}`,
                    answer: q.content,
                  },
                });
              }
            });
          });

          const sortedChapters = response.chapters.slice().sort((p, n) => {
            return p.order_number - n.order_number;
          });

          response.chapters = sortedChapters;

          state.currentModule = response;
        }
      })
      .addCase(removeGuidance.fulfilled, (state, action) => {
        if ("error" in action) return;
        if (!action.payload || !state.currentModule) return;

        const { guidanceId, current } = action.payload;

        state.currentModule.chapters.map((c) => {
          c.questions.map((q) => {
            if (q.question_id === current) {
              q.guidances = q.guidances.filter(
                (g) =>
                  !(g.guidance_id === guidanceId || g.frontId === guidanceId)
              );
            }
            q.possible_answers.map((a) => {
              if (a.possible_answer_id === current) {
                a.guidances = a.guidances.filter(
                  (g) =>
                    !(g.guidance_id === guidanceId || g.frontId === guidanceId)
                );
              }
            });
          });
        });
      })
      .addCase(removeExamples.fulfilled, (state, action) => {
        if ("error" in action) return;
        if (!action.payload || !state.currentModule) return;

        const { current, exampleId } = action.payload;

        state.currentModule.chapters.map((c) => {
          c.questions.map((q) => {
            if (q.question_id === current) {
              q.examples = q.examples.filter(
                (g) => !(g.example_id === exampleId || g.frontId === exampleId)
              );
            }
            q.possible_answers.map((a) => {
              if (a.possible_answer_id === current) {
                a.examples = a.examples.filter(
                  (g) =>
                    !(g.example_id === exampleId || g.frontId === exampleId)
                );
              }
            });
          });
        });
      })
      .addCase(removeStep, (state, { payload }) => {
        if (!state.currentModule) return;
        const { chapter, pos } = payload;

        let index = 0;

        state.currentModule.chapters.forEach((c) => {
          if (c.order_number === chapter) {
            c.questions.forEach((q, i) => {
              if (q.order_number === pos) index = i;
            });
          }
        });

        state.currentModule.chapters.forEach((c) => {
          c.questions.splice(index, 1);
        });

        state.currentModule.chapters.forEach((c) => {
          if (c.order_number === chapter) {
            c.questions.forEach((q) => {
              if (q.order_number >= pos) q.order_number--;
            });
          }
          c.questions.sort((p, n) => {
            return p.order_number - n.order_number;
          });
        });
      })
      .addCase(removeChapter, (state, { payload }) => {
        if (!state.currentModule) return;
        const { chapterPos } = payload;
        let index = 0;

        state.currentModule.chapters.forEach((c, i) => {
          if (c.order_number === chapterPos) index = i;
        });

        state.currentModule.chapters.splice(index, 1);

        state.currentModule.chapters.forEach((c) => {
          if (c.order_number >= chapterPos) c.order_number--;
        });
      })
      .addCase(addAlways, (state, { payload }) => {
        if (!state.currentModule) return;

        const { chapter, step } = payload;

        const question = state.currentModule.chapters[chapter].questions[step];

        if (question.question_id && !state.newBeEvents[question.question_id]) {
          const snapshot =
            state.currentModule.chapters[chapter].questions[step];
          if (
            typeof state.currentModule.chapters[chapter].questions[step]
              .content === "string"
          ) {
            snapshot.content =
              state.currentModule.chapters[chapter].questions[step].content;
          }

          state.newBeEvents[question.question_id] = {
            event: "patch",
            type: "question",
            snapshot: JSON.stringify(snapshot),
          };
        }

        question.trigger = {
          and: [{ or: ["always"] }],
        };
      })
      .addCase(negateTrigger, (state, { payload }) => {
        if (!state.currentModule) return;

        const { directions, step, chapter } = payload;
        const condition =
          state.currentModule.chapters[chapter].questions[step].trigger;

        const questionId =
          state.currentModule.chapters[chapter].questions[step].question_id;

        if (questionId && !state.newBeEvents[questionId]) {
          state.newBeEvents[questionId] = {
            event: "patch",
            type: "question",
            snapshot: JSON.stringify(
              state.currentModule.chapters[chapter].questions[step]
            ),
          };
        }

        toggleTrigger(directions, condition);
      })
      .addCase(addTrigger, (state, { payload }) => {
        if (!state.currentModule) return;
        const { directions, step, chapter, trigger } = payload;
        const condition =
          state.currentModule.chapters[chapter].questions[step].trigger;

        writeTrigger(directions, trigger, condition);
      })
      .addCase(setShowPopUp, (state) => {
        state.showPopUp = !state.showPopUp;
        state.popUpType = null;
      })
      .addCase(setShowPopUpOnly, (state) => {
        state.showPopUp = !state.showPopUp;
      })
      .addCase(setClosePopUp, (state) => {
        state.showPopUp = false;
      })
      .addCase(resetPopUpType, (state) => {
        state.popUpType = null;
      })
      .addCase(setPopupType, (state) => {
        state.popUpType = "Term";
      })
      .addCase(createUserManual.fulfilled, (state, action) => {
        const { response } = action.payload;
        if (state.currentModule && response) {
          state.currentModule.user_manual_term_id = response.data.user_manual_term_id;
        }
      })
      .addCase(setProducerUserManualCreation, (state, {payload}) => {
        state.userManualCreation = payload.flag;
      })
      .addCase(changeMode, (state, { payload }) => {
        state.mode = payload.mode;
      });
  },
});

export const {
  setSearchBarShowing,
  setLinkToAdd,
  setRefToAdd,
  setTermToAdd,
  setConditionToChange,
  removeFrontAnswers
} = producerSlice.actions;

export default producerSlice.reducer;

/**
   @function initializeModule
   initialize module loading it and setting stor
   @param orgId {string} - organization id
   @param moduleId {string} -module to load
*/
export const initializeModule = createAsyncThunk(
  "producer/initializeModule",
  async ({ orgId, moduleId }: { orgId: string; moduleId: string }) => {
    try {
      const response = await getModule({
        organizationId: orgId,
        moduleId,
        isUpdated: true,
      });
      return {
        response,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function addQuestionToBackend
   add new question
   @param orgId {string} - organization id
   @param moduleId {string} -module to change
   @param frontQuestionId {string} - id saved in frontend
   @param order_number {number} - order_number of chapter
   @param name {string} - name to save the chapter with 
*/
export const addQuestionToBackend = createAsyncThunk(
  "producer/addQuestion",
  async ({
    orgId,
    order_number,
    content,
    chapter_id,
    conclusion,
    multiple,
    print,
    should_be_hidden,
    trigger,
    comment,
    display_index,
    frontQuestionId,
  }: PayloadPostQuestion & { frontQuestionId: string }) => {
    try {
      const translatedContent =
        typeof content === "string"
          ? content
          : "text" in content
            ? content
            : { text: JSONToArray(content.content ? content.content : {} as StepContent) };

      const response = await postQuestion({
        orgId,
        chapter_id,
        order_number,
        conclusion,
        display_index,
        multiple,
        print,
        should_be_hidden,
        trigger,
        comment,
        content: translatedContent,
      });

      return {
        frontQuestionId: frontQuestionId,
        response: response,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function isTriggerInCondition
   returns if trigger is in condition
   @param trigger {object} - trigger to search for
   @param condition {object} - condition to search
   @returns {boolean}
*/
function isTriggerInConditionAndAddDeleteCallBack(
  trigger: any,
  condition: Trigger,
  result: any
) {
  const triggerAction = (
    element: any,
    recursiveResult: any,
    stepName: any,
    previousTrigger: any
  ) => {
    if (typeof element === "object" && "chapter" in element) {
      if (
        (element.chapter === trigger.chapter &&
          element.question === trigger.question &&
          element.answer === trigger.answer) ||
        (element.chapter === trigger.chapter &&
          element.question === trigger.question)
      ) {
        recursiveResult.result = true;

        if ("and" in previousTrigger) {
          previousTrigger.and = previousTrigger.and.filter(
            (t: any) =>
              (t.chapter !== trigger.chapter &&
                t.question !== trigger.question &&
                t.answer !== trigger.answer) ||
              (t.chapter !== trigger.chapter && t.question !== trigger.question)
          );
        }
        if ("or" in previousTrigger) {
          previousTrigger.or = previousTrigger.or.filter(
            (t: any) =>
              (t.chapter !== trigger.chapter &&
                t.question !== trigger.question &&
                t.answer !== trigger.answer) ||
              (t.chapter !== trigger.chapter && t.question !== trigger.question)
          );
        }
      }
    } else {
      return isTriggerInConditionAndAddDeleteCallBack(
        trigger,
        element,
        recursiveResult
      );
    }
  };
  return navigateTriggers(condition, result, null, triggerAction);
}

/**
   @function addChapterToBackend
   add new chapter
   @param orgId {string} - organization id
   @param moduleId {string} -module to change
   @param frontChapterId {string} - id saved in frontend
   @param order_number {number} - order_number of chapter
   @param name {string} - name to save the chapter with 
*/
export const addChapterToBackend = createAsyncThunk(
  "producer/addChapter",
  async ({
    orgId,
    moduleId,
    frontChapterId,
    order_number,
    name,
  }: {
    orgId: string;
    moduleId: string;
    frontChapterId: string;
    order_number: number;
    name: string;
  }) => {
    try {
      const response = await postChapter({
        orgId,
        moduleId,
        name,
        orderNumber: order_number,
      });

      return {
        frontChapterId: frontChapterId,
        response: response,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function addPossibleAnswerToBackend
   add new possible answer
   @param orgId {string} - organization id
   @param moduleId {string} -module to change
   @param frontChapterId {string} - id saved in frontend
   @param order_number {number} - order_number of chapter
   @param name {string} - name to save the chapter with 
*/
export const addPossibleAnswerToBackend = createAsyncThunk(
  "producer/addPossibleAnswer",
  async ({
    orgId,
    frontAnswerId,
    order_number,
    content,
    question_id,
    single,
    comment,
    newBeEvents,
  }: PayloadPostPossibleAnswer & {
    orgId: string;
    frontAnswerId: string;
    newBeEvents: BackendEvents;
  }) => {
    try {
      const translatedContent =
        typeof content === "string"
          ? content
          : "text" in content
            ? content
            : { text: JSONToArray(content.content ? content.content : {} as StepContent) };

      const response = await postPossibleAnswers({
        orgId,
        order_number,
        content: translatedContent,
        single,
        question_id,
        comment,
      });

      const guidanceRes: Array<{
        frontId: any;
        res: any;
      }> = [];
      // TODO: the code below user `guidances` which is not even defined
      if (question_id) {
        const guidances = newBeEvents[question_id]?.guidances;
        if (guidances) {
          for (let x = 0; x < guidances.length; x++) {
            const toSave = guidances[x];

            for (let y = 0; y < guidances.length; y++) {
              const guide = guidances[y];
              if (typeof guide !== "string") {
                const translatedGuideContent = typeof guide.content == "object" && "text" in guide.content ? JSONToArray(guide.content) : JSONToArray(guide.content.content ? guide.content.content : {} as StepContent);
                if (guide.frontId === toSave) {
                  await postGuidance({
                    orgId,
                    order_number: guide.order_number,
                    content: {
                      text: translatedGuideContent,
                    },
                    questionId: question_id,
                    possibleAnswerId: null,
                  }).then((r) => {
                    guidanceRes.push({ res: r.data, frontId: toSave });
                  });
                }
                if (guide.guidance_id === toSave) {
                  await patchGuidance({
                    orgId,
                    order_number: guide.order_number,
                    content: {
                      text: translatedGuideContent,
                    },
                    questionId: question_id,
                    guidance_id: guide.guidance_id,
                  });
                }
              }
            }
          }
        }
      }

      const exampleRes: Array<{
        frontId: any;
        res: any;
      }> = [];
      // TODO: the code below uses `examples` which is not even defined
      if (question_id) {
        const examples = newBeEvents[question_id]?.examples;
        if (examples) {
          for (let x = 0; x < examples.length; x++) {
            const toSave = examples[x];

            for (let y = 0; y < examples.length; y++) {
              const example = examples[y];
              if (typeof example !== "string") {
                const translatedExampleContent = typeof example.content == "object" && "text" in example.content ? JSONToArray(example.content) : JSONToArray(example.content.content ? example.content.content : {} as StepContent);
                if (example.frontId === toSave) {
                  await postExample({
                    orgId,
                    order_number: example.order_number,
                    content: {
                      text: translatedExampleContent,
                    },
                    questionId: question_id,
                    possibleAnswerId: null,
                  }).then((r) => {
                    exampleRes.push({ res: r.data, frontId: toSave });
                  });
                }
                if (example.example_id === toSave) {
                  await patchExample({
                    orgId,
                    order_number: example.order_number,
                    content: {
                      text: translatedExampleContent,
                    },
                    questionId: question_id,
                    possibleAnswerId: null,
                    example_id: example.example_id,
                  });
                }
              }
            }
          }
        }
      }

      return {
        frontAnswerId: frontAnswerId,
        response: response,
        guidanceRes: guidanceRes,
        exampleRes: exampleRes,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function removeChapterToBackend
   remove
   @param orgId {string} - organization id
   @param chapterId {string} - id saved in frontend
*/
export const removeChapterInBackend = createAsyncThunk(
  "producer/removeChapter",
  async ({
    orgId,
    chapterId,
    frontChapterId,
  }: {
    orgId: string;
    chapterId: string;
    frontChapterId: string;
  }) => {
    try {
      if ((chapterId !== undefined && chapterId !== "") && (frontChapterId == "" || frontChapterId !== chapterId)) {
        await deleteChapter({ orgId, chapterId });
      }

      return {
        chapterId,
        frontChapterId,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function removeChapterToBackend
   remove
   @param orgId {string} - organization id
   @param chapterId {string} - id saved in frontend
   @param frontQuestionId {string} - front id
*/
export const removeQuestionInBackend = createAsyncThunk(
  "producer/removeQuestion",
  async ({
    orgId,
    questionId,
    frontQuestionId,
  }: {
    orgId: string;
    questionId: string;
    frontQuestionId: string;
  }) => {
    try {
      if ((questionId !== undefined && questionId !== "") && (frontQuestionId == "" || frontQuestionId !== questionId)) {
        await deleteQuestion({ orgId, questionId });
      }

      return {
        questionId: questionId,
        frontQuestionId: frontQuestionId,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function removePossibleAnswerInBackend
   remove
   @param orgId {string} - organization id
   @param chapterId {string} - id saved in frontend
*/
export const removePossibleAnswerInBackend = createAsyncThunk(
  "producer/removePossibleAnswer",
  async ({
    orgId,
    possibleAnswerId,
    frontAnswerId,
  }: {
    orgId: string;
    possibleAnswerId: string;
    frontAnswerId: string;
  }) => {
    try {
      if ((possibleAnswerId !== undefined && possibleAnswerId !== "") && (frontAnswerId == "" || frontAnswerId !== possibleAnswerId)) {
        await deletePossibleAnswer({
          orgId,
          possibleAnswerId,
        });
      }

      return {
        possibleAnswerId: possibleAnswerId,
        frontAnswerId: frontAnswerId,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function changeChapterInBackend
   change chapter in backend
   @param orgId {string} - organization id
   @param moduleId {string} -module to change
   @param chapterId {string} - id saved in backend
   @param order_number {number} - order_number of chapter
   @param name {string} - name to save the chapter with 
*/
export const changeChapterInBackend = createAsyncThunk(
  "producer/changeChapter",
  async ({
    orgId,
    moduleId,
    chapterId,
    order_number,
    name,
  }: {
    orgId: string;
    moduleId: string;
    chapterId: string;
    order_number: number;
    name: string;
  }) => {
    try {
      const response = await patchChapter({
        orgId,
        moduleId,
        chapterId,
        name,
        orderNumber: order_number,
      });

      return {
        chapterId: chapterId,
        response: response,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function removeGuidance
   remove guidance from backend or local
   @param guidanceId {string} - guidance id
*/
export const removeGuidance = createAsyncThunk(
  "producer/removeGuidance",
  async ({
    id,
    isInBackend,
    current,

    orgId,
  }: {
    id: string;
    isInBackend?: boolean;
    current?: string;

    orgId: string;
  }) => {
    try {
      if (isInBackend) {
        deleteGuidance({ orgId, guidanceId: id });
      }

      return {
        guidanceId: id,
        current,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function removeExample
   remove example from backend or local
   @param exampleId {string} - example id
*/
export const removeExamples = createAsyncThunk(
  "producer/removeExamples",
  async ({
    id,
    isInBackend,
    current,

    orgId,
  }: {
    id: string;
    isInBackend?: boolean;
    current?: string;

    orgId: string;
  }) => {
    try {
      if (isInBackend) {
        deleteExample({ orgId, exampleId: id });
      }
      return {
        exampleId: id,
        current,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function changeQuestionInBackend
   change chapter in backend
   @param orgId {string} - organization id
   @param moduleId {string} -module to change
   @param chapterId {string} - id saved in backend
   @param order_number {number} - order_number of chapter
   @param name {string} - name to save the chapter with 
   @param newBeEvents {object} - events for backend
*/
export const changeQuestionInBackend = createAsyncThunk(
  "producer/changeQuestion",
  async ({
    orgId,
    questionId,
    order_number,
    content,
    chapterId,
    conclusion,
    displayId,
    multiple,
    print,
    should_be_hidden,
    trigger,
    guidances,
    examples,
    newBeEvents,
    comment,
  }: {
    orgId: string;
    questionId: string;
    order_number?: number;
    content: string | Record<string, any>;
    chapterId?: string;
    conclusion: boolean;
    displayId?: number;
    multiple: boolean;
    print: boolean;
    should_be_hidden: boolean;
    trigger: Trigger;
    guidances: GuidanceOrExample[];
    examples: GuidanceOrExample[];
    newBeEvents: BackendEvents;
    comment?: string | null;
  }) => {
    try {
      const translatedContent =
        typeof content === "string"
          ? content
          : "text" in content
          ? content
          : { text: JSONToArray(content.content) };

      await patchQuestion({
        orgId,
        question_id: questionId,
        order_number,
        content: translatedContent,
        conclusion,
        display_index: displayId,
        multiple,
        print,
        should_be_hidden,
        trigger,
        chapter_id: chapterId,
        comment,
      });

      const guidanceRes: Array<{
        frontId: any;
        res: any;
      }> = [];

      if (questionId && newBeEvents[questionId]) {
        const guidanciesInBeEvents = newBeEvents[questionId].guidances;
        if (guidanciesInBeEvents !== undefined) {
          for (let x = 0; x < guidanciesInBeEvents.length; x++) {
            const toSave = guidanciesInBeEvents[x];

            for (let y = 0; y < guidances.length; y++) {
              const guide = guidances[y];
              const translatedGuideContent = typeof guide.content == "object" && "text" in guide.content ? JSONToArray(guide.content) : JSONToArray(guide.content.content ? guide.content.content : {} as StepContent);
              if (guide.frontId === toSave) {
                await postGuidance({
                  orgId,
                  order_number: guide.order_number,
                  content: {
                    text: translatedGuideContent,
                  },
                  questionId,
                  possibleAnswerId: null,
                }).then((r) => {
                  guidanceRes.push({ res: r.data, frontId: toSave });
                });
              }
              if (guide.guidance_id === toSave) {
                await patchGuidance({
                  orgId,
                  order_number: guide.order_number,
                  content: {
                    text: translatedGuideContent,
                  },
                  questionId,
                  guidance_id: guide.guidance_id,
                });
              }
            }
          }
        }
      }

      const exampleRes: Array<{
        frontId: any;
        res: any;
      }> = [];
      if (questionId && newBeEvents[questionId].examples !== undefined) {
        const examplesInBeEvents = newBeEvents[questionId].examples;
        if (examplesInBeEvents) {
          for (let x = 0; x < examplesInBeEvents.length; x++) {
            const toSave = examplesInBeEvents[x];

            for (let y = 0; y < examples.length; y++) {
              const example = examples[y];
              const translatedExampleContent = typeof example.content == "object" && "text" in example.content ? JSONToArray(example.content) : JSONToArray(example.content.content ? example.content.content : {} as StepContent);
              if (example.frontId === toSave) {
                await postExample({
                  orgId,
                  order_number: example.order_number,
                  content: {
                    text: translatedExampleContent,
                  },
                  questionId,
                  possibleAnswerId: null,
                }).then((r) => {
                  exampleRes.push({ res: r.data, frontId: toSave });
                });
              }
              if (example.example_id === toSave) {
                await patchExample({
                  orgId,
                  order_number: example.order_number,
                  content: {
                    text: translatedExampleContent,
                  },
                  questionId,
                  possibleAnswerId: null,
                  example_id: example.example_id,
                });
              }
            }
          }
        }
      }

      return {
        questionId: questionId,
        guidanceRes: guidanceRes,
        chapterId: chapterId,
        exampleRes: exampleRes,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function changePossibleAnswerToBackend
   change  possible answer
   @param orgId {string} - organization id
   @param moduleId {string} -module to change
   @param frontChapterId {string} - id saved in frontend
   @param order_number {number} - order_number of chapter
   @param name {string} - name to save the chapter with 
*/
export const changePossibleAnswerToBackend = createAsyncThunk(
  "producer/changePossibleAnswer",
  async ({
    orgId,
    possibleAnswerId,
    order_number,
    content,
    questionId,
    single,
    guidances,
    examples,
    newBeEvents,
    comment,
  }: {
    orgId: string;
    possibleAnswerId: string;
    order_number: number;
    content: string | StepContent;
    questionId: string;
    single: boolean;
    guidances: GuidanceOrExample[];
    examples: GuidanceOrExample[];
    newBeEvents: Record<string, any>;
    comment?: string | null;
  }) => {
    try {
      const translatedContent =
        typeof content === "string"
          ? content
          : "text" in content
            ? content
            : { text: JSONToArray(content.content ? content.content : {} as StepContent) };


      await patchPossibleAnswers({
        orgId,
        orderNumber: order_number,
        content: translatedContent,
        single,
        questionId,
        possibleAnswerId,
        comment,
      });

      const guidanceRes: Array<{
        frontId: any;
        res: any;
      }> = [];
      if (newBeEvents[possibleAnswerId].guidances !== undefined) {
        for (
          let x = 0;
          x < newBeEvents[possibleAnswerId].guidances.length;
          x++
        ) {
          const toSave = newBeEvents[possibleAnswerId].guidances[x];

          for (let y = 0; y < guidances.length; y++) {
            const guide = guidances[y];
            const translatedGuideContent = typeof guide.content == "object" && "text" in guide.content ? JSONToArray(guide.content) : JSONToArray(guide.content.content ? guide.content.content : {} as StepContent);
            if (guide.frontId === toSave && guide.guidance_id === undefined) {
              await postGuidance({
                orgId,
                order_number: guide.order_number,
                content: {
                  text: translatedGuideContent,
                },
                questionId: null,
                possibleAnswerId,
              }).then((r) => {
                guidanceRes.push({ res: r.data, frontId: toSave });
              });
            }
            if (guide.guidance_id === toSave) {
              await patchGuidance({
                orgId,
                order_number: guide.order_number,
                content: {
                  text: translatedGuideContent,
                },
                questionId: null,
                possible_answer_id: possibleAnswerId,
                guidance_id: guide.guidance_id,
              });
            }
          }
        }
      }

      const exampleRes: Array<{
        frontId: any;
        res: any;
      }> = [];
      if (newBeEvents[possibleAnswerId].guidances !== undefined) {
        for (
          let x = 0;
          x < newBeEvents[possibleAnswerId].examples.length;
          x++
        ) {
          const toSave = newBeEvents[possibleAnswerId].examples[x];

          for (let y = 0; y < examples.length; y++) {
            const example = examples[y];
            const translatedExampleContent = typeof example.content == "object" && "text" in example.content ? JSONToArray(example.content) : JSONToArray(example.content.content ? example.content.content : {} as StepContent);
            if (
              example.frontId === toSave &&
              example.example_id === undefined
            ) {
              await postExample({
                orgId,
                order_number: example.order_number,
                content: {
                  text: translatedExampleContent,
                },
                questionId: null,
                possibleAnswerId,
              }).then((r) => {
                exampleRes.push({ res: r.data, frontId: toSave });
              });
            }

            if (example.example_id === toSave) {
              await patchExample({
                orgId,
                order_number: example.order_number,
                content: {
                  text: translatedExampleContent,
                },
                questionId: null,
                possibleAnswerId,
                example_id: example.example_id,
              });
            }
          }
        }
      }

      return {
        possibleAnswerId: possibleAnswerId,
        questionId: questionId,
        exampleRes: exampleRes,
        guidanceRes: guidanceRes,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function createUserManual
   Sends patch call to patch user manual for module
   @param orgId {string} - org id
   @param termId {string} - user_manual_term id
   @param moduleId {string} -module to load
*/
export const createUserManual = createAsyncThunk(
  "producer/createUserManual",
  async ({ orgId, moduleId, termId }: { orgId: string; moduleId: string; termId : string; }) => {
    try {
      const response = await patchUserManual({
        orgId,
        moduleId,
        termId
      });

      return {
        response: response,
      };
    } catch (error) {
      return {
        error: error,
      };
    }
  }
);

/**
   @function idGenerator
   @returns {string}
*/
function idGenerator() {
  return (Math.random() + 1).toString(36).substring(2);
}

const selectProducer = (state: RootState) => state.producer;

export const selectCurrentModule = createSelector(
  selectProducer,
  ({ currentModule }) => currentModule
);

export const selectRichTextFocus = createSelector(
  selectProducer,
  ({ richTextFocus }) => richTextFocus
);

export const selectQuestionFromCurrentModuleByChapterAndQuestion =
  createSelector(
    selectCurrentModule,
    (
      _: RootState,
      params: {
        chapterIndex: number;
        questionIndex: number;
      }
    ) => params,
    (currentModule, { chapterIndex, questionIndex }) => {
      if (currentModule && currentModule.chapters) {
        const chapter = currentModule.chapters[chapterIndex];
        if (chapter) {
          const question = chapter.questions[questionIndex];
          return question;
        }
      }
      return null;
    }
  );

export const selectProducerNewBeEvents = createSelector(
  selectProducer,
  ({ newBeEvents }) => newBeEvents
);

export const selectCurrentChapter = createSelector(
  selectProducer,
  ({ currentChapter }) => currentChapter
);

export const selectTriggers = createSelector(
  selectProducer,
  ({ triggers }) => triggers
);

export const selectPopUpType = createSelector(
  selectProducer,
  ({ popUpType }) => popUpType
);

export const selectTermToAdd = createSelector(
  selectProducer,
  ({ termToAdd }) => termToAdd
);

export const selectLinkToAdd = createSelector(
  selectProducer,
  ({ linkToAdd }) => linkToAdd
);

export const selectRefToAdd = createSelector(
  selectProducer,
  ({ refToAdd }) => refToAdd
);

export const selectTriggerToAdd = createSelector(
  selectProducer,
  ({ triggerToAdd }) => triggerToAdd
);

export const selectSearchBarShowing = createSelector(
  selectProducer,
  ({ searchBarShowing }) => searchBarShowing
);

export const selectShowAddElement = createSelector(
  selectProducer,
  ({ showAddElement }) => showAddElement
);

export const selectShowPopUp = createSelector(
  selectProducer,
  ({ showPopUp }) => showPopUp
);

export const selectDeleteElement = createSelector(
  selectProducer,
  ({ deleteElement }) => deleteElement
);

export const selectConditionToChange = createSelector(
  selectProducer,
  ({ conditionToChange }) => conditionToChange
);

export const selectCurrentStep = createSelector(
  selectProducer,
  ({ currentStep }) => currentStep
);

export const selectUserManualCreation = createSelector(
  selectProducer,
  ({ userManualCreation }) => userManualCreation
);